import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, ImagePlus, Loader2, MessageCircle, Paperclip, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/shared/api/client';
import { useIsReadOnly } from '@/shared/auth/SessionProvider';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Textarea } from '@/shared/components/ui/textarea';
import { cn } from '@/shared/lib/cn';
import {
  bitacoraPhotoUrl,
  createBitacora,
  listBitacorasForVenta,
  type BitacoraPhoto,
  type BitacoraRow,
} from '../api';

/**
 * Bitácoras panel used by both the row-level quick popup and the
 * detail page Bitácoras action. Shows the service-log
 * history for a venta and lets admin add a new entry with an optional
 * photo. Uses multipart upload + Graph DriveItem storage (not the
 * legacy Base64-in-list scheme).
 *
 * Legacy rows with `legacyBase64` render via data-URI directly; new
 * rows render via the `/api/mantenimiento/photo/:driveItemId` stream.
 */
export function BitacorasTab({ ventaId }: { ventaId: string }) {
  const isReadOnly = useIsReadOnly();
  const qc = useQueryClient();
  const bitacorasQ = useQuery({
    queryKey: ['mantenimiento', 'bitacoras', ventaId],
    queryFn: () => listBitacorasForVenta(ventaId),
    staleTime: 30_000,
  });
  const [descripcion, setDescripcion] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      createBitacora({
        ventaId,
        descripcion: descripcion.trim(),
        photo,
      }),
    onSuccess: () => {
      toast.success('Bitácora agregada');
      setDescripcion('');
      setPhoto(null);
      qc.invalidateQueries({ queryKey: ['mantenimiento', 'bitacoras', ventaId] });
    },
    onError: (err) =>
      toast.error('No se pudo agregar la bitácora', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      }),
  });

  const canSubmit = descripcion.trim().length > 0 && !mutation.isPending;

  return (
    <div className="flex flex-col gap-4">
      {!isReadOnly && (
        <AddBitacoraForm
          descripcion={descripcion}
          onDescripcionChange={setDescripcion}
          photo={photo}
          onPhotoChange={setPhoto}
          onSubmit={() => mutation.mutate()}
          submitting={mutation.isPending}
          canSubmit={canSubmit}
        />
      )}
      <BitacorasList
        rows={bitacorasQ.data ?? []}
        loading={bitacorasQ.isPending}
        error={bitacorasQ.isError}
      />
    </div>
  );
}

function AddBitacoraForm({
  descripcion,
  onDescripcionChange,
  photo,
  onPhotoChange,
  onSubmit,
  submitting,
  canSubmit,
}: {
  descripcion: string;
  onDescripcionChange: (v: string) => void;
  photo: File | null;
  onPhotoChange: (f: File | null) => void;
  onSubmit: () => void;
  submitting: boolean;
  canSubmit: boolean;
}) {
  const [preview, setPreview] = useState(false);
  return (
    <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
      <Textarea
        rows={2}
        placeholder="Agregar observación, comentario o reporte de servicio…"
        value={descripcion}
        onChange={(e) => onDescripcionChange(e.target.value)}
        maxLength={2000}
        disabled={submitting}
      />
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <label
            className={cn(
              'inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md border px-2.5 text-sm text-muted-foreground transition-colors hover:bg-secondary',
              submitting && 'pointer-events-none opacity-50',
            )}
          >
            <ImagePlus className="h-4 w-4" aria-hidden />
            {photo ? 'Cambiar foto' : 'Adjuntar foto'}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => onPhotoChange(e.target.files?.[0] ?? null)}
              disabled={submitting}
            />
          </label>
          {photo && (
            <div className="flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-[11px] text-muted-foreground">
              <Paperclip className="h-3 w-3" aria-hidden />
              <button
                type="button"
                onClick={() => setPreview(true)}
                title="Vista previa"
                className="max-w-48 truncate text-left text-foreground hover:underline"
                disabled={submitting}
              >
                {photo.name}
              </button>
              <button
                type="button"
                onClick={() => setPreview(true)}
                aria-label={`Vista previa ${photo.name}`}
                title="Vista previa"
                className="rounded p-0.5 hover:bg-muted"
                disabled={submitting}
              >
                <Eye className="h-3 w-3" aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => onPhotoChange(null)}
                aria-label="Quitar foto"
                title="Quitar"
                className="rounded p-0.5 hover:bg-muted"
                disabled={submitting}
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </div>
          )}
        </div>
        <Button onClick={onSubmit} disabled={!canSubmit} size="sm" className="gap-1.5">
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Send className="h-4 w-4" aria-hidden />
          )}
          Agregar
        </Button>
      </div>
      {photo && preview && (
        <PhotoPreviewDialog file={photo} onClose={() => setPreview(false)} />
      )}
    </div>
  );
}

/**
 * Inline preview for a staged bitácora photo. Same blob-URL pattern
 * used in the despacho/pago dialogs — created on mount, revoked on
 * unmount so we don't leak memory if the user opens preview repeatedly.
 */
function PhotoPreviewDialog({
  file,
  onClose,
}: {
  file: File;
  onClose: () => void;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="truncate">{file.name}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {!blobUrl ? (
            <div className="flex h-64 items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            </div>
          ) : (
            <img
              src={blobUrl}
              alt={file.name}
              className="mx-auto max-h-[70vh] w-auto rounded-lg"
            />
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} className="gap-2">
            <X className="h-4 w-4" aria-hidden />
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BitacorasList({
  rows,
  loading,
  error,
}: {
  rows: BitacoraRow[];
  loading: boolean;
  error: boolean;
}) {
  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
        No se pudieron cargar las bitácoras.
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-md border border-dashed px-4 py-8 text-sm text-muted-foreground">
        <MessageCircle className="h-5 w-5" aria-hidden />
        Sin entradas en la bitácora todavía.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {rows.map((r) => (
        <BitacoraCard key={r.id} row={r} />
      ))}
    </div>
  );
}

function BitacoraCard({ row }: { row: BitacoraRow }) {
  const f = row.fields;
  return (
    <div className="rounded-md border bg-card p-3 text-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="whitespace-pre-wrap break-words text-foreground">
            {f.DescripcionBitacora_BB || '—'}
          </p>
        </div>
        <div className="shrink-0 text-right text-[11px] text-muted-foreground">
          <div>{f.FechaBitacora_BB || '—'}</div>
          {f.Hora_BB && <div>{f.Hora_BB}</div>}
          {f.User_BB && <div className="mt-0.5 font-medium">{f.User_BB}</div>}
        </div>
      </div>
      {row.photos.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {row.photos.map((p) => (
            <BitacoraPhotoThumb key={p.id} photo={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function BitacoraPhotoThumb({ photo }: { photo: BitacoraPhoto }) {
  const [open, setOpen] = useState(false);
  if (!photo.driveItemId && !photo.legacyBase64) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Vista previa"
        className="inline-flex items-center gap-1.5 rounded-md border bg-background px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
      >
        <Paperclip className="h-3.5 w-3.5" aria-hidden />
        <span>Foto</span>
        <Eye className="h-3.5 w-3.5" aria-hidden />
      </button>
      {open && (
        <StoredPhotoPreviewDialog photo={photo} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

/**
 * Authenticated preview for a stored bitácora photo. The
 * `/api/mantenimiento/photo/:driveItemId` endpoint requires a bearer
 * token, but `<img src>` doesn't carry our `Authorization` header — so
 * we have to fetch the bytes ourselves and reconstitute as a blob URL.
 * Legacy rows store the image inline as base64; that case skips the
 * fetch and uses the data URI directly.
 */
function StoredPhotoPreviewDialog({
  photo,
  onClose,
}: {
  photo: BitacoraPhoto;
  onClose: () => void;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (photo.legacyBase64) {
      setSrc(photo.legacyBase64);
      return;
    }
    if (!photo.driveItemId) {
      setError('La foto no tiene un identificador asociado.');
      return;
    }
    let cancelled = false;
    let createdUrl: string | null = null;
    (async () => {
      try {
        const res = await fetch(bitacoraPhotoUrl(photo.driveItemId!), {
          headers: authHeaders(),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setSrc(createdUrl);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Error desconocido');
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [photo.driveItemId, photo.legacyBase64]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Foto de bitácora</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {error ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              No se pudo cargar la foto. {error}
            </div>
          ) : !src ? (
            <div className="flex h-64 items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            </div>
          ) : (
            <img
              src={src}
              alt="Bitácora"
              className="mx-auto max-h-[70vh] w-auto rounded-lg"
            />
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} className="gap-2">
            <X className="h-4 w-4" aria-hidden />
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
